import { format } from "date-fns";
import { ProjectService } from "../../services/ProjectService";
import { getProfileCategoryList } from "../profileCategory";
import { getProjectCategoryList } from "../projectCategory";
import { getRoleTypeList } from "../roleType";
import {
  SET_LOADING,
  SET_SELECTED_ROLES,
  SET_LOCATION_LIST,
  SET_FORM_ROLE_TYPE,
  SET_AUTO_FILL_DESCRIPTION,
  SET_GENERATED_PROJECT,
} from "./types";
import dayjs from "dayjs";
import { setSelectedRecruiter } from "../recruiter";

/**
 * @author Sarthak Sharma.
 * @since 04-01-2024.
 * @description to set loading
 */
export const setLoading = (data) => ({
  type: SET_LOADING,
  payload: data,
});

/**
 * @author Sarthak Sharma.
 * @since 06-01-2024.
 * @description to set Role list
 */
export const setSelectedRoles = (data) => ({
  type: SET_SELECTED_ROLES,
  payload: data,
});

/**
 * @author Sarthak Sharma.
 * @since 06-01-2024.
 * @description to set Location list
 */
export const setLocationList = (data) => ({
  type: SET_LOCATION_LIST,
  payload: data,
});

export const setFormRoleType = (data) => ({
  type: SET_FORM_ROLE_TYPE,
  payload: data,
});

export const setAutofillDescription = (data) => {
  return {
    type: SET_AUTO_FILL_DESCRIPTION,
    payload: data,
  };
};

export const setGeneratedProject = (data) => ({
  type: SET_GENERATED_PROJECT,
  payload: data,
});

/**
 * @author Sarthak Sharma.
 * @since 06-01-2024.
 * @description to get Location list
 */
export const getLocationList =
  (successCallback, errorCallback) => async (dispatch) => {
    await dispatch(setLoading(true));
    await ProjectService.getLocationList()
      .then(async (res) => {
        console.log("Result: ", res);
        await dispatch(setLocationList(res?.data[0] ?? []));
        successCallback?.();
      })
      .catch(async (err) => {
        console.log("Location list Error: ", err);
        errorCallback?.(err);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

export const generateProject =
  (autofillDescription, successCallback, errorCallback) => async (dispatch) => {
    dispatch(setLoading(true));

    await ProjectService.generateProject(autofillDescription)
      .then(async (res) => {
        if (res?.data?.length > 0) {
          dispatch(setGeneratedProject(res?.data[0]));
          successCallback?.();
        } else {
          dispatch(setGeneratedProject({}));
        }
      })
      .catch((err) => {
        console.error("Generated Project Error: ", err);
        errorCallback?.(err);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

export const fetchInitialAuditionData =
  (successCallback, errorCallback) => async (dispatch) => {
    let promises = [];
    promises.push(dispatch(getProjectCategoryList(() => {}, errorCallback)));
    promises.push(
      dispatch(
        getProfileCategoryList(
          { categoryType: "Artist" },
          () => {},
          errorCallback
        )
      )
    );
    promises.push(dispatch(getLocationList(() => {}, errorCallback)));
    promises.push(dispatch(getRoleTypeList(() => {}, errorCallback)));
    return Promise.all(promises)
      .then((res) => successCallback())
      .catch((err) => console.log(err))
      .finally(() => dispatch(setLoading(null)));
  };

export const addAiRollData = (data) => async (dispatch) => {
  const rolesArray = Object.keys(data)
    .map((roleKey) => {
      const roleDataRaw = data[roleKey];

      if (!roleDataRaw) {
        console.error("Invalid data format for roleKey:", roleKey);
        return null;
      }

      const shootDate = roleDataRaw?.shootDate
        ? new Date(roleDataRaw?.shootDate)
        : undefined;

      let experienceCategory = roleDataRaw?.experienceCategory
        ? roleDataRaw.experienceCategory
        : "";

      const validCategories = [
        "CELEBRITIES",
        "EXPERIENCED",
        "INTERMEDIATE",
        "BEGINNER",
      ];
      if (Array.isArray(experienceCategory)) {
        experienceCategory = experienceCategory.join(",");
      }
      if (experienceCategory) {
        const categories = experienceCategory.split(",");
        const validList = categories.filter((category) =>
          validCategories.includes(category.trim())
        );

        experienceCategory = validList.length > 0 ? validList.join(",") : "";
      } else {
        experienceCategory = "";
      }

      let language =
        roleDataRaw?.language && roleDataRaw?.language !== "NONE"
          ? roleDataRaw.language
          : "";

      if (Array.isArray(language)) {
        language = language.join(", ");
      }

      return {
        isAiGenerated: true,
        profileCategory: {
          id: roleDataRaw?.profileCategory?.id ?? "",
        },
        roleType: {
          id: roleDataRaw?.roleType?.id ?? "",
        },
        title: roleDataRaw?.title ?? "",
        gender: roleDataRaw?.gender ?? "",
        minAge: roleDataRaw?.minAge ?? 10,
        maxAge: roleDataRaw?.maxAge ?? 60,
        language: language,
        experienceCategory: experienceCategory,
        shootLocation: roleDataRaw?.shootLocation ?? "",
        shootDate: shootDate,
        auditionLocation: roleDataRaw?.auditionLocation ?? "",
        characterDescription: roleDataRaw?.characterDescription ?? "",
        numberOfDays: Number(roleDataRaw?.numberOfDays) || "",
        payBasis: roleDataRaw?.payBasis ?? "",
        minBudget: Number(roleDataRaw?.minBudget) || "",
        maxBudget: Number(roleDataRaw?.maxBudget) || "",
        negotiable: roleDataRaw?.negotiable ?? false,
        notes: roleDataRaw?.notes ?? "",
        isSelfTestCheck: roleDataRaw?.isSelfTestCheck ?? false,
        script: roleDataRaw?.selfTest?.recruiterSelfTest?.script ?? "",
        isActor: roleDataRaw?.isActor ?? false,
        isModel: roleDataRaw?.isModel ?? false,
        isInfluencer: roleDataRaw?.isInfluencer ?? false,
        isExpectedBudget: roleDataRaw?.isExpectedBudget ?? false,
        jobType: roleDataRaw?.jobType ?? "",
        storedVideos: roleDataRaw?.storedVideos ?? [],
        storedImages: roleDataRaw?.storedImages ?? [],
        storedAudios: roleDataRaw?.storedAudios ?? [],
        storedPdf: roleDataRaw?.storedPdf ?? [],
        profileCategories: roleDataRaw?.profileCategories ?? [],
      };
    })
    .filter((role) => role !== null);

  await dispatch(setSelectedRoles(rolesArray));
  console.log("Ai------>>>>", rolesArray);
};

export const addNewRollData =
  (data, prevRoleList, callBack) => async (dispatch) => {
    const shootDate = data.shootDate
      ? dayjs(data.shootDate).format("MM-DD-YYYY")
      : "";

    const roleData = {
      profileCategory: {
        id: data.profileCategoryId,
      },
      roleType: {
        id: data.roleTypeId,
      },
      title: data.title,
      gender: data.gender,
      minAge: data.ageRange[0],
      maxAge: data.ageRange[1],
      language: data.language?.length ? data?.language?.join(", ") : "",
      experienceCategory: data.experienceCategory?.length
        ? data.experienceCategory.join(",")
        : "",
      shootLocation: data.shootLocation,
      shootDate: shootDate,
      auditionLocation: data.auditionLocation,
      characterDescription: data.characterDescription,
      numberOfDays: data.numberOfDays,
      payBasis: data.payBasis,
      minBudget: +data.minBudget,
      maxBudget: +data.maxBudget,
      isActor: data.isActor,
      isModel: data.isModel,
      isInfluencer: data.isInfluencer,
      isExpectedBudget: data.isExpectedBudget,
      negotiable: data.negotiable,
      notes: data.notes,
      selfTestCheck: data.selfTestCheck,
      script: data.script,
      jobType: data.jobType,
      storedVideos: data.storedVideos,
      storedImages: data.storedImages,
      storedAudios: data.storedAudios,
      storedPdf: data.storedPdf,
      profileCategories: data.profileCategories ?? [],
    };

    console.log("Add------>>>>", roleData);

    await dispatch(setSelectedRoles([...prevRoleList, roleData]));
    callBack();
  };

export const removeRollData =
  (index, prevRoleList, callBack) => async (dispatch) => {
    prevRoleList.splice(index, 1);
    await dispatch(setSelectedRoles([...prevRoleList]));
    callBack();
  };

export const updateRollData =
  (data, prevRoleList, callBack) => async (dispatch) => {
    const shootDate = data.shootDate
      ? dayjs(data.shootDate).format("MM-DD-YYYY")
      : "";

    const roleData = {
      profileCategory: {
        id: data.profileCategoryId,
      },
      roleType: {
        id: data.roleTypeId,
      },
      title: data.title,
      gender: data.gender,
      minAge: data.ageRange[0],
      maxAge: data.ageRange[1],
      language: data.language?.length ? data?.language?.join(", ") : "",
      experienceCategory: data.experienceCategory?.length
        ? data.experienceCategory.join(",")
        : "",
      shootLocation: data.shootLocation,
      shootDate: shootDate,
      auditionLocation: data.auditionLocation,
      characterDescription: data.characterDescription,
      numberOfDays: data.numberOfDays,
      payBasis: data.payBasis,
      minBudget: +data.minBudget,
      maxBudget: +data.maxBudget,
      isActor: data.isActor,
      isModel: data.isModel,
      isInfluencer: data.isInfluencer,
      isExpectedBudget: data.isExpectedBudget,
      negotiable: data.negotiable,
      selfTestCheck: data.selfTestCheck,
      script: data.script,
      jobType: data.jobType,
      storedVideos: data.storedVideos,
      storedImages: data.storedImages,
      storedAudios: data.storedAudios,
      storedPdf: data.storedPdf,
      profileCategories: data.profileCategories ?? [],
    };
    console.log("Update------>>>>", roleData);

    prevRoleList[data.index] = roleData;
    await dispatch(setSelectedRoles([...prevRoleList]));
    callBack();
  };

export const updateRollTitle = (data, prevRoleList) => async (dispatch) => {
  prevRoleList[data.index] = { ...prevRoleList[data.index], title: data.title };
  await dispatch(setSelectedRoles([...prevRoleList]));
};

export const updateJobType = (data, prevRoleList) => async (dispatch) => {
  prevRoleList[data.index] = {
    ...prevRoleList[data.index],
    jobType: data.jobType,
  };
  await dispatch(setSelectedRoles([...prevRoleList]));
};

/**
 * @author Sarthak Sharma.
 * @since 02-01-2024.
 * @description to add project data
 */
export const addProjectData =
  (data, successCallback, errorCallback) => async (dispatch) => {
    const formData = new FormData();

    const roles = data.rolesList.map((role) => {
      (role?.storedImages ?? []).forEach((item) => {
        formData.append("images", item);
      });

      let audios = role?.storedAudios ?? [];
      audios.forEach((item) => {
        formData.append("audios", item);
      });

      (role?.storedPdf ?? []).forEach((item) => {
        formData.append("pdfs", item);
      });

      return {
        profileCategory: { id: role.profileCategory.id },
        profileCategories: role.profileCategories,
        roleType: role.roleType,
        experienceCategory: role.experienceCategory,
        gender: role.gender,
        minAge: role.minAge,
        maxAge: role.maxAge,
        language: role.language,
        isSelfTest: false,
        characterDescription: role.characterDescription,
        notes: role.notes,
        description: "",
        shootLocation: role.shootLocation,
        shootDate: role.shootDate,
        auditionLocation: role.auditionLocation,
        numberOfDays: role.numberOfDays,
        minBudget: +role.minBudget,
        maxBudget: +role.maxBudget,
        payBasis: role.payBasis,
        title: role?.title,
        jobType: role.jobType,
        negotiable: role.negotiable,
        isSelfTestCheck: role.selfTestCheck,
        selfTest: role.selfTestCheck
          ? {
              recruiterSelfTest: {
                script: role.script,
                referenceVideo: role.storedVideos,
                referencePhoto: role.storedImages.map((a) => ({
                  mediaId: a.name,
                })),
                referenceVoiceNote: audios.map((a) => ({ mediaId: a.name })),
                pdfs: role.storedPdf.map((a) => ({ mediaId: a.name })),
                askExpectedBudget: role.isExpectedBudget,
              },
            }
          : undefined,
      };
    });

    const request = {
      userId: data.userId,
      name: data.name,
      title: data.title,
      description: data.description,
      projectCategory: { id: data.projectCategoryId },
      isdraft: data.isdraft,
      startDate: format(new Date(), "MM-dd-yyyy"),
      endDate: format(new Date(data?.endDate), "MM-dd-yyyy"),
      rolesList: roles,
    };

    console.log("Request------>>>>", request);

    formData.append("project", JSON.stringify(request));

    formData.forEach((value, key) => {
      console.log(`${key}: ${value}`);
    });

    dispatch(setLoading(true));

    await ProjectService.addProject(formData)
      .then(async (res) => {
        console.log("Result: ", res);
        dispatch(setSelectedRoles([]));
        dispatch(setSelectedRecruiter(null));
        successCallback(res);
      })
      .catch(async (err) => {
        console.log("Error in adding project: ", err);
        errorCallback(err);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };
